import { h, Component, Fragment } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';
import linkstate from 'linkstate';
import md5 from 'crypto-js/md5';

import fields from '../../lib/fields';

import FormFields from '../../components/form/fields';
import ImageScroller from '../../components/imagescroller';
import HelpText from '../../components/gui/helpText';
import Input from '../../components/form/input';
import GuiList from '../../components/gui/list';

import localUtil from '../../lib/util';
import DateTile from '../../components/gui/dateTile';
import formatRelativeDays from '../../lib/relativeDays';
import '../../components/gui/tileList.css';

@withText(props => ({
    upcomingTitleText: <Text id='convention.upcoming-conventions'>Upcoming conventions</Text>,
    pastTitleText: <Text id='convention.past-conventions'>Past conventions</Text>,
}))
@observer
class Convention extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            sessionid: new Date().getTime(),
            sections: fields.get('contact'),
        };
        this.mainContainer = null;
    }

    // Tab and search live on conventionStore (listTab/listSearch), not in
    // component state — this component unmounts when the user opens a
    // convention, and both must survive the round-trip back to the list.
    selectTab = (tab) => () => {
        const { conventionStore } = this.props.stores;
        conventionStore.updateKeyValue('listTab', tab);
    }

    onListSearch = (search) => {
        const { conventionStore } = this.props.stores;
        conventionStore.updateKeyValue('listSearch', search);
    }

    newConvention = e => {
        const { id, title } = e.target.closest('button').dataset;
        const { drawerLevel = 1 } = this.props;
		const { appState, timetrackersStore } = this.props.stores;
		const { drawerHeightLarge } = appState;
		appState.openDrawer('conventionWizard', {
			height: drawerHeightLarge,
            callback: () => {
                appState.toggleDrawer();
                this.loadAll(this.props, false);
            },
		}, drawerLevel + 2);
	}

    viewConvention = (e, props) => {
        const { view } = this.props;
        if (view) {
            return view(e);
        }
        const { id, title } = props ? props : e.target.closest('.box-line').dataset;
        return route(`/convention/${id}`);

        // const { appState } = this.props.stores;
        // const { drawerLevel = 1 } = this.props;
        // const { drawerHeightLarge } = appState;
        // appState.openDrawer('viewConvention', {
        //     id,
        //     height: drawerHeightLarge,
        //     // editFunction: this.editTimeConvention,
        //     callback: () => {
        //         appState.toggleDrawer();
        //         this.loadAll(this.props, false);
        //     },
        // }, drawerLevel + 1);
    }

    renderDateTile = obj => (
        <DateTile date={obj.startDate} isPublic={!!obj.public} isCancelled={!!obj.isCancelled} />
    )

    viewTags = obj => {
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const tagClass = darkmode ? 'dark' : 'light';

        // Stats (views, trend, signups, results) are shown in the detail view,
        // not in the list rows.
        const hasTags = obj.tags?.length > 0;
        const hasFiles = obj.files?.length > 0;

        if (!hasTags && !hasFiles && !obj.loans?.length && !obj.ownedByOther && !obj.url && !obj.parents?.length) {
            return '';
        }

        return (<>
            <div class='w-100 overflow-hidden'>
                <div class='box-line-tags d-flex flex-nowrap'>
                    {hasFiles && <>
                        <span class={`badge text-bg-light me-1 mt-1`}>
                            <i class='fa-duotone fa-file' /> <span class='font-weight-lighter'> x {obj.files.length}</span>
                        </span>
                    </>}
                    {hasTags && obj.tags.map(tag => {
                        return (<>
                            <span
                                class={`badge text-bg-${tagClass} me-1 mt-1`}
                                onClick={this.toggleTagFilter}
                                data-tag={tag.name}
                            >#{tag.name}</span>
                        </>);
                    })}
                </div>
            </div>
        </>);
    }

    loadAll = async (props = this.props, emptyFirst = false) => {
        const { id, todayOnly, openRegistrations } = props;
        const { conventionStore } = this.props.stores;
        if (emptyFirst) {
            conventionStore.updateKeyValue('conventions', []);
        }
        conventionStore.setRefreshPage(() => {
            this.loadAll();
        });
        await conventionStore.load(id, false, {
            query: {
                hostname: conventionStore.currentHostname,
                todayOnly,
                openRegistrations,
            },
            addData: ['tags', 'members'],
        });

        const { convention } = conventionStore;
        conventionStore.updateKeyValue('newConvention', {
            title: convention.title,
            ingress: convention.ingress,
            body: convention.body,
            address: convention.address,
            postalcode: convention.postalcode,
            place: convention.place,
            images: convention.images,
        });
    }

    filter = obj => {
        if (!obj) {
            return false;
        }

        const { giveMeAll } = this.props;
        const { showAll } = this.state;

        // If showAll is true, return the object to include it in the results
        if (showAll || giveMeAll) {
            return true;
        }

        // Get the current date and set the time to the start of the day
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);
        // Get the current date and set the time to the end of the day
        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);

        const { todayOnly } = this.props;
        if (todayOnly) {
            // Check if startDate exists and is today
            if (obj.startDate && new Date(obj.startDate).toDateString() === new Date().toDateString()) {
                return true;
            }
            return false;
        }


        // Check if endDate exists and is before or equal to the end of the day
        if (obj.endDate && new Date(obj.endDate) <= endOfDay) {
            return true;
        }

        // Exclude the object from the results
        return false;
    }

    toggleShowAll = () => {
        this.setState({ showAll: !this.state.showAll });
    }

    viewAllConventions = () => {
        route('/convention');
    }

    gotoConvention = e => {
        const { id } = e.target.closest('div').dataset;
        route(`/convention/${id}`);
    }

    componentDidMount() {
        this.loadAll();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.page !== this.props.page || nextProps.artid !== this.props.artid) {
            this.loadAll(nextProps);
        }
    }

    formatRelativeDays = date => formatRelativeDays(date)

    render() {
        const { showAll } = this.state;
        const {
            classNames = '',
            title = 'Stevner / treninger',
            children,
            todayOnly,
            showGlobalButton,
            openRegistrations,
            hideHero,
            hideEmpty,
            upcomingTitleText,
            pastTitleText,
        } = this.props;

        const { appState, userStore, conventionStore } = this.props.stores;
        const { mainView, subView, isDevelopment, path } = appState;
        const { user = {}, isAdmin, isCustomerAdmin, isTester, isVeterinary, language } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const { conventions = [], listTab, listSearch } = conventionStore;

        const finalList = conventions && conventions.length ? conventions.filter(this.filter) : [];

        // hideEmpty hides the entire box — including the admin add/filter buttons —
        // unlike GuiList's hideEmpty below, which admins bypass.
        if (hideEmpty && finalList.length === 0) {
            return (<></>);
        }

        // Split into upcoming (today + future) and past for clearer reading order.
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const upcomingList = [];
        const pastList = [];
        finalList.forEach(c => {
            const refDate = c.endDate || c.startDate;
            if (refDate && new Date(refDate) >= startOfToday) {
                upcomingList.push(c);
            } else {
                pastList.push(c);
            }
        });
        upcomingList.sort((a, b) => new Date(a.startDate || 0).getTime() - new Date(b.startDate || 0).getTime());
        pastList.sort((a, b) => new Date(b.startDate || 0).getTime() - new Date(a.startDate || 0).getTime());
        const splitMode = upcomingList.length > 0 && pastList.length > 0;

        // Real competitions (have an NSF competition number) that finished within the last 7
        // days. Surfaced on the upcoming tab so users don't have to hunt for the Ferdige tab
        // to find a just-finished event. Training/dugnad without an nsfNumber are excluded.
        // pastList is already sorted newest-first, so no extra sort is needed here.
        const sevenDaysAgo = new Date(startOfToday);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const recentlyFinishedList = pastList.filter(c => {
            if (!(c.nsfNumber > 0)) {
                return false;
            }
            const refDate = c.endDate || c.startDate;
            return refDate && new Date(refDate) >= sevenDaysAgo;
        });

        // Default tab: upcoming if any future events exist, otherwise past
        const tab = listTab || (upcomingList.length > 0 ? 'upcoming' : 'past');

        // todayOnly (frontpage box): today's events get their own highlighted cards,
        // independent of the Kommende/Ferdige tab remembered from the /convention list.
        // The startDate check keeps the «I dag» label true even when the admin filter
        // toggle (showAll) lets a stale non-today row from the store through.
        const todayString = new Date().toDateString();
        const todayList = todayOnly
            ? finalList
                .filter(c => c.startDate && new Date(c.startDate).toDateString() === todayString)
                .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
            : [];
        const todayTimeOpts = { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' };

        return (<>
            {todayOnly && showGlobalButton ? <>
                {conventions.length > 0 ? <>
                    <div
                        class='position-absolute rounded-pill w-75 d-flex flex-row justify-content-center align-items-center bg-success text-white py-1'
                        style='
                            z-index: 999999;
                            bottom: 170px;
                            left: 50%;
                            transform: translateX(-50%);
                            font-size: 0.9rem;
                            line-height: 1.0rem;
                            cursor: pointer;
                            max-width: 450px;
                        '
                    >
                        <div
                            class={`d-flex flex-row justify-content-center align-items-center ms-1 me-1 mt-1 px-1 py-1 overflow-hidden`}
                            onClick={this.gotoConvention}
                            data-id={conventions[0].id}
                        >
                            <span style='font-size: 1.3rem;'>
                                <i class='fa-duotone fa-flag fa-bounce me-2' />
                            </span>
                            {util.formatDate(conventions[0].startDate,{
                                hour12: false,
                                hour: '2-digit',
                                minute: '2-digit',
                                day: '2-digit',
                                month: 'short',
                                year: '2-digit',
                                locale: 'nb-NO',
                            }, true)}: {conventions[0].title}
                        </div>
                    </div>
                </> : <></>}
            </> : <>
                <div class={`d-flex justify-content-start flex-row w-100 position-relative`}>
                    <div class='flex-fill'>
                        {/* <h5 class='ms-3 mb-3'>{convention.title}</h5> */}
                    </div>
                    {isCustomerAdmin && <div class={`position-absolute`} style={`top: 15px; right: 5px; z-index: 1000;`}>
                        <button onClick={this.newConvention} class={`btn btn-sm btn-outline-success rounded-pill me-2`}>
                            <i class={`fa-regular fa-plus`} />
                        </button>
                        <button onClick={this.toggleShowAll} class={`btn btn-sm btn${showAll ? '' : '-outline'}-success rounded-pill me-2`}>
                            <i class={`fa-regular fa-filter`} />
                        </button>
                    </div>}
                    {/* <div class='' style='margin-right: 65px;'>
                        <span onClick={this.editConvention} class={`text-primary ${linkTextClass}`} style={`${linkTextStyle}`}>
                            <i class={`fa-regular fa-pen ms-3 me-1`} />
                        </span>
                    </div> */}
                </div>

                {todayOnly && todayList.length > 0 && (
                    <div class={`w-100 d-flex flex-column ${classNames}`}>
                        <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1'>
                            <Text id='convention.today-title'>Conventions / trainings today</Text>
                        </div>
                        {todayList.map(c => {
                            const isCancelled = !!c.isCancelled;
                            const start = new Date(c.startDate);
                            const startTime = util.formatDate(c.startDate, todayTimeOpts, true);
                            const endTime = c.endDate && new Date(c.endDate).toDateString() === start.toDateString()
                                ? util.formatDate(c.endDate, todayTimeOpts, true)
                                : '';
                            return (
                                <div
                                    key={c.id}
                                    class={`d-flex flex-row align-items-center mx-3 mb-2 px-3 py-3 rounded-lg shadow text-white ${isCancelled ? 'bg-secondary' : 'bg-violet'}`}
                                    style='cursor: pointer;'
                                    onClick={() => route(`/convention/${c.id}`)}
                                >
                                    <div
                                        class='d-flex flex-column justify-content-center align-items-center rounded bg-white text-center me-3'
                                        style='width: 64px; min-width: 64px; height: 64px; line-height: 1;'
                                    >
                                        <span
                                            class={`fw-bold ${isCancelled ? 'text-secondary' : ''}`}
                                            style={`font-size: 0.75rem; letter-spacing: 0.05em;${isCancelled ? '' : ' color: #6610f2;'}`}
                                        >
                                            {start.toLocaleDateString('nb-NO', { month: 'short' }).replace(/\.$/, '').toUpperCase()}
                                        </span>
                                        <span class='text-dark fw-bold' style='font-size: 1.7rem;'>
                                            {start.getDate()}
                                        </span>
                                    </div>
                                    <div class='flex-fill' style='min-width: 0;'>
                                        <div class='small text-uppercase fw-bold mb-1' style='letter-spacing: 0.05em;'>
                                            <Text id='convention.today-label'>Today</Text> · {startTime}{endTime && endTime !== startTime ? `–${endTime}` : ''}
                                        </div>
                                        <div class='fw-bold' style='font-size: 1.4rem; line-height: 1.2;'>
                                            {c.title || ''}
                                        </div>
                                        {(isCancelled || !!c.isCompetition) && <div class='mt-1'>
                                            {isCancelled && <span class='badge text-bg-danger me-1'>
                                                <i class='fa-solid fa-ban me-1' /><Text id='convention.cancelled'>Cancelled</Text>
                                            </span>}
                                            {!!c.isCompetition && <span class='badge text-bg-light me-1'>
                                                <i class='fa-solid fa-trophy me-1' /><Text id='convention.competition'>Competition</Text>
                                            </span>}
                                        </div>}
                                    </div>
                                    <i class='fa-solid fa-chevron-right ms-3' />
                                </div>
                            );
                        })}
                    </div>
                )}

                {!todayOnly && (upcomingList.length > 0 || pastList.length > 0) && (
                    <ul class='nav nav-pills justify-content-center mb-3'>
                        <li class='nav-item'>
                            <button
                                type='button'
                                class={`nav-link ${tab === 'upcoming' ? 'active' : ''}`}
                                onClick={this.selectTab('upcoming')}
                            >
                                <Text id='convention.upcoming-tab'>Kommende</Text>
                                {upcomingList.length > 0 && (
                                    <span class='badge bg-dark text-white ms-2'>{upcomingList.length}</span>
                                )}
                            </button>
                        </li>
                        <li class='nav-item'>
                            <button
                                type='button'
                                class={`nav-link ${tab === 'past' ? 'active' : ''}`}
                                onClick={this.selectTab('past')}
                            >
                                <Text id='convention.past-tab'>Ferdige</Text>
                                {pastList.length > 0 && (
                                    <span class='badge bg-dark text-white ms-2'>{pastList.length}</span>
                                )}
                            </button>
                        </li>
                        <li class='nav-item'>
                            <a href='/convention-rankings' class='nav-link'>
                                <i class='fa-duotone fa-ranking-star me-1' />
                                <Text id='convention.rankings-link'>Ranking lists</Text>
                            </a>
                        </li>
                    </ul>
                )}

                {!todayOnly && tab === 'upcoming' && !hideHero && recentlyFinishedList.length > 0 && (
                    <div class='mx-3 mb-4'>
                        <div class='d-flex align-items-center justify-content-between mb-2'>
                            <div class='text-body-secondary small fw-bold'>
                                <i class='fa-duotone fa-circle-check me-1' />
                                <span class='text-uppercase'>
                                    <Text id='convention.recently-finished'>Recently finished</Text>
                                </span>
                                <span class='text-body-tertiary fw-normal ms-1'>
                                    · <Text id='convention.recently-finished-window'>last 7 days</Text>
                                </span>
                            </div>
                            <button
                                type='button'
                                class='btn btn-sm btn-link text-decoration-none p-0'
                                onClick={this.selectTab('past')}
                            >
                                <Text id='convention.see-all-finished'>See all finished</Text>
                                <i class='fa-solid fa-arrow-right ms-1' />
                            </button>
                        </div>
                        <div class='list-group shadow-sm'>
                            {recentlyFinishedList.map(c => {
                                const refDate = c.endDate || c.startDate;
                                const rel = this.formatRelativeDays(refDate);
                                return (
                                    <button
                                        type='button'
                                        class='list-group-item list-group-item-action d-flex align-items-center'
                                        onClick={() => route(`/convention/${c.id}`)}
                                    >
                                        <DateTile date={refDate} isCancelled />
                                        <span class='flex-fill text-truncate ms-3'>{c.title || ''}</span>
                                        {rel && (
                                            <span class='badge text-bg-light' style='font-size: 0.7rem;'>{rel}</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {!todayOnly && tab === 'upcoming' && !hideHero && upcomingList.length > 0 && (() => {
                    const hero = upcomingList[0];
                    const heroImage = hero.images?.[0]?.s3MediumLink;
                    const heroRelative = this.formatRelativeDays(hero.startDate);
                    return (
                        <div
                            class={`d-flex flex-row align-items-center mx-3 mb-4 mt-2 px-3 py-3 rounded-lg border border-2 border-success ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}
                            style='cursor: pointer;'
                            onClick={() => route(`/convention/${hero.id}`)}
                        >
                            <div class='me-3 d-flex align-items-center justify-content-center' style='width: 80px; height: 80px; flex-shrink: 0; overflow: hidden; border-radius: 10px; background: rgba(0,0,0,0.05);'>
                                {heroImage ? (
                                    <img src={heroImage} alt={hero.title || ''} class='w-100 h-100' style='object-fit: cover;' />
                                ) : (
                                    <DateTile date={hero.startDate} isPublic={!!hero.public} isCancelled={!!hero.isCancelled} />
                                )}
                            </div>
                            <div class='flex-fill'>
                                <div class='text-success small text-uppercase fw-bold mb-1'>
                                    <i class='fa-solid fa-bullseye-arrow me-1' />
                                    <Text id='convention.next-up'>Next up</Text>
                                </div>
                                <h4 class='mb-1' style='line-height: 1.2;'>
                                    {!!hero.isCancelled && <span class='badge text-bg-danger me-2 align-middle'>
                                        <i class='fa-solid fa-ban me-1' />Avlyst
                                    </span>}
                                    {hero.title || ''}
                                </h4>
                                <div class='d-flex flex-wrap align-items-center'>
                                    {hero.startDate && (
                                        <span class='text-body-secondary me-2'>
                                            <i class='fa-duotone fa-calendar me-1' />
                                            {util.formatDate(hero.startDate, {
                                                hour12: false,
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                day: '2-digit',
                                                month: 'short',
                                                year: new Date(hero.startDate).getFullYear() !== new Date().getFullYear() ? '2-digit' : undefined,
                                                locale: 'nb-NO',
                                            }, true)}
                                        </span>
                                    )}
                                    {heroRelative && (
                                        <span class='badge text-bg-success' style='font-size: 0.85rem;'>
                                            <i class='fa-duotone fa-hourglass-half me-1' />
                                            {heroRelative}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {!todayOnly && tab === 'upcoming' && (hideHero ? upcomingList.length > 0 : upcomingList.length > 1) && (
                    <GuiList
                        stores={this.props.stores}
                        holdingStore={conventionStore}
                        classNames={classNames}
                        styles={''}
                        category='conventions'
                        classNamesList={'mx-0 mb-3 tile-list'}
                        view={this.viewConvention}
                        title={upcomingTitleText}
                        initialSearch={listSearch}
                        searchCallback={this.onListSearch}
                        list={hideHero ? upcomingList : upcomingList.slice(1)}
                        elTitle={e => e.title || 'No client'}
                        elInfo1={e => e.startDate ? <>
                            <span class='text-secondary'>
                                {util.formatDate(e.startDate, {
                                    hour12: false,
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    day: '2-digit',
                                    month: 'short',
                                    year: new Date(e.startDate).getFullYear() !== new Date().getFullYear() ? '2-digit' : undefined,
                                    locale: 'nb-NO',
                                }, true)}
                                {(() => {
                                    const rel = this.formatRelativeDays(e.startDate);
                                    return rel ? <span class='badge text-bg-secondary ms-2' style='font-size: 0.7rem;'>{rel}</span> : '';
                                })()}
                            </span>
                        </> : ''}
                        elInfo2={e => <>
                            {!!e.isCancelled && <span class={`badge text-bg-danger me-1`} style='font-size: 0.7rem;'>
                                <i class='fa-solid fa-ban me-1' />Avlyst
                            </span>}
                            {e.type === 'dugnad' && <span class='badge text-bg-light me-1' style='font-size: 0.7rem;' title='Dugnad'>
                                <i class='fa-duotone fa-people-carry-box me-1' />Dugnad
                            </span>}
                            {e.isCompetition ? <>
                                <span class={`badge text-bg-danger me-1`} style='font-size: 0.7rem;' title='Konkurranse'>
                                    <i class='fa-solid fa-trophy' />
                                </span>
                                {e.nsfNumber ? <>
                                    <span class={`badge text-bg-light me-1`} style='font-size: 0.7rem;'>
                                        {e.nsfNumber}
                                    </span>
                                </> : ''}
                            </> : ''}
                        </>}
                        elInfo3={e => e.stages?.length > 0 ? <>
                            <span class='badge text-bg-light' style='font-size: 0.75rem;'>
                                <i class='fa-duotone fa-flag me-1' />{e.stages.length} lag
                            </span>
                        </> : ''}
                        elIconFunc={this.renderDateTile}
                        iconStyle='width: 52px; min-width: 52px;'
                        viewTags={this.viewTags}
                        hideEmpty={isAdmin ? false : true}
                        editElement={this.editConvention}
                        help={children}
                        showEmptyImage={false}
                    />
                )}

                {!todayOnly && tab === 'past' && <GuiList
                    stores={this.props.stores}
                    holdingStore={conventionStore}
                    classNames={classNames}
                    styles={''}
                    category='conventions'
                    classNamesList={'mx-0 mb-3 tile-list'}
                    view={this.viewConvention}
                    title={upcomingList.length > 0 ? pastTitleText : title}
                    initialSearch={listSearch}
                    searchCallback={this.onListSearch}
                    // title={todayOnly ? title : title}
                    // filter={this.filter}
                    list={pastList}
                    elTitle={e => e.title || 'No client'}
                    elInfo1={e => e.startDate ? <>
                        <span class='text-secondary'>
                            {util.formatDate(e.startDate, {
                                hour12: false,
                                hour: '2-digit',
                                minute: '2-digit',
                                day: '2-digit',
                                month: 'short',
                                year: new Date(e.startDate).getFullYear() !== new Date().getFullYear() ? '2-digit' : undefined,
                                locale: 'nb-NO',
                            }, true)}
                            {(() => {
                                const rel = this.formatRelativeDays(e.startDate);
                                return rel ? <span class='badge text-bg-secondary ms-2' style='font-size: 0.7rem;'>{rel}</span> : '';
                            })()}
                        </span>
                    </> : ''}
                    elInfo2={e => <>
                        {!!e.isCancelled && <span class={`badge text-bg-danger me-1`} style='font-size: 0.7rem;'>
                            <i class='fa-solid fa-ban me-1' />Avlyst
                        </span>}
                        {e.type === 'dugnad' && <span class='badge text-bg-light me-1' style='font-size: 0.7rem;' title='Dugnad'>
                            <i class='fa-duotone fa-people-carry-box me-1' />Dugnad
                        </span>}
                        {e.isCompetition ? <>
                            <span class={`badge text-bg-danger me-1`} style='font-size: 0.7rem;' title='Konkurranse'>
                                <i class='fa-solid fa-trophy' />
                            </span>
                            {e.nsfNumber ? <>
                                <span class={`badge text-bg-light me-1`} style='font-size: 0.7rem;'>
                                    {e.nsfNumber}
                                </span>
                            </> : ''}
                        </> : ''}
                    </>}
                    elInfo3={e => e.stages?.length > 0 ? <>
                        <span class='badge text-bg-light' style='font-size: 0.75rem;'>
                            <i class='fa-duotone fa-flag me-1' />{e.stages.length} lag
                        </span>
                    </> : ''}

                    elIconFunc={this.renderDateTile}
                    iconStyle='width: 52px; min-width: 52px;'
                    viewTags={this.viewTags}
                    hideEmpty={isAdmin ? false : true}
                    editElement={this.editConvention}
                    help={children}
                    showEmptyImage={false}
                />}
            </>}
        </>);
    }
}

export default Convention;
